import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { RespostaLancamentos } from '../services/api';
import { colors } from '../theme/expertiseTheme';

/**
 * Componente de resumo dos lançamentos
 */
type Props = {
  dados: RespostaLancamentos | null;
  onDownload: () => void;
  loading: boolean;
};

const ResumoLancamentos: React.FC<Props> = ({ dados, onDownload, loading }) => {
  if (!dados) {
    return null;
  }

  // Calcula o valor total a ser pago
  // Soma dos valorTotal de lancamentos (pessoas com PIX) menos soma dos valorTotal de lancamentosSemPix
  const valorTotalComPix = dados.lancamentos?.reduce((sum, lanc) => sum + (lanc.valorTotal || 0), 0) || 0;
  const valorTotalSemPix = dados.lancamentosSemPix?.reduce((sum, lanc) => sum + (lanc.valorTotal || 0), 0) || 0;
  const valorTotalAPagar = valorTotalComPix - valorTotalSemPix;

  // Formata o valor em moeda brasileira
  const formatarValor = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(valor);
  };

  const estatisticas = [
    {
      titulo: 'Total de Lançamentos',
      valor: dados.total,
      icon: <ReceiptIcon sx={{ fontSize: 28 }} />,
      cor: colors.primary,
      bgColor: 'rgba(0, 99, 0, 0.1)',
    },
    {
      titulo: 'Total de Pessoas',
      valor: dados.totalPessoas,
      icon: <PeopleIcon sx={{ fontSize: 28 }} />,
      cor: '#7C3AED',
      bgColor: 'rgba(124, 58, 237, 0.1)',
    },
    {
      titulo: 'Pessoas COM PIX',
      valor: dados.pessoasComPix,
      icon: <CheckCircleIcon sx={{ fontSize: 28 }} />,
      cor: colors.primary,
      bgColor: 'rgba(0, 99, 0, 0.1)',
    },
    {
      titulo: 'Pessoas SEM PIX',
      valor: dados.pessoasSemPix,
      icon: <WarningIcon sx={{ fontSize: 28 }} />,
      cor: dados.pessoasSemPix > 0 ? '#F59E0B' : colors.primary,
      bgColor: dados.pessoasSemPix > 0 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(0, 99, 0, 0.1)',
    },
    {
      titulo: 'Valor Total a Pagar',
      valor: formatarValor(valorTotalAPagar),
      icon: <AttachMoneyIcon sx={{ fontSize: 28 }} />,
      cor: '#059669',
      bgColor: 'rgba(5, 150, 105, 0.1)',
    },
  ];

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        mb: 3,
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 3,
          fontWeight: 600,
          color: colors.dark,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box 
          sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            backgroundColor: colors.accent 
          }} 
        />
        Resumo dos Dados
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {estatisticas.map((stat, index) => (
          <Grid 
            item 
            xs={12} 
            sm={6} 
            md={index === 4 ? 12 : 3} 
            lg={index === 4 ? false : true}
            sx={index === 4 ? { 
              '@media (min-width: 1200px)': { 
                maxWidth: '20%', 
                flexBasis: '20%' 
              } 
            } : {}}
            key={index}
          >
            <Card
              sx={{
                height: '100%',
                border: `1px solid ${colors.border}`,
                borderRadius: '12px',
                boxShadow: 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: stat.cor,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 16px rgba(0, 0, 0, 0.08)`,
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Box 
                    sx={{ 
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: stat.bgColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: stat.cor,
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700, 
                    mb: 0.5,
                    color: colors.dark,
                  }}
                >
                  {stat.valor}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: colors.text.secondary,
                    fontSize: '0.8125rem',
                  }}
                >
                  {stat.titulo}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {dados.pessoasSemPix > 0 && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            borderRadius: '8px',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            '& .MuiAlert-icon': {
              color: '#F59E0B',
            },
          }}
        >
          <Typography variant="body2" sx={{ color: colors.dark }}>
            <strong>Percentual sem PIX:</strong> {dados.percentualSemPix?.toFixed(2)}%
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<DownloadIcon />}
          onClick={onDownload}
          disabled={loading || dados.pessoasComPix === 0}
          sx={{
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            backgroundColor: colors.primary,
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: '#004d00',
            },
            '&:disabled': {
              backgroundColor: colors.border,
              color: colors.text.light,
            },
          }}
        >
          Download Arquivo CNAB240 ({dados.pessoasComPix} pagamentos)
        </Button>

        {dados.pessoasSemPix > 0 && (
          <Alert 
            severity="info" 
            variant="outlined"
            sx={{
              borderRadius: '8px',
              borderColor: colors.border,
            }}
          >
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              O arquivo conterá apenas <strong>{dados.pessoasComPix} pessoa(s)</strong> com chave PIX cadastrada.
            </Typography>
          </Alert>
        )}

        {dados.pessoasComPix === 0 && (
          <Alert 
            severity="error"
            sx={{
              borderRadius: '8px',
            }}
          >
            <Typography variant="body2">
              Não há nenhuma pessoa com chave PIX cadastrada para gerar o arquivo.
            </Typography>
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default ResumoLancamentos;
